import { createSelector } from 'reselect'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import { decodeSearchParams } from 'lib/search/searchUtils'
import { isString } from 'lib/string/stringUtils'
import { EmptyArray, last } from 'lib/array/arrayUtils'
import { LOYALTY_SEARCH_FILTER_PARAM } from 'loyalty/constants/loyaltySearchFilters'
import { matchPath } from 'react-router'

export const getUrlOfferListFilters = createSelector(
  (state: App.State) => state.router.location.search,
  (search): App.OfferListFilters => {
    const params = parseSearchString(search)
    const searchParams = decodeSearchParams(search)

    return {
      locations: params.locations ? params.locations.split(',') : [],
      holidayTypes: params.holidayTypes ? params.holidayTypes.split(',') : [],
      holidayTypesScoped: params.holidayTypesScoped ? params.holidayTypesScoped.split(',') : [],
      propertyTypes: params.propertyTypes ? params.propertyTypes.split(',') : [],
      campaigns: params.campaigns ? params.campaigns.split(',') : [],
      sortBy: params.sortBy as App.OfferListSortByOptions,
      strategyApplied: params.strategyApplied,
      destinationId: params.destinationId?.split(',')[0], // First destination ID (single)
      destinationIds: params.destinationId ? params.destinationId.split(',').filter(Boolean) : undefined, // All destination IDs (array)
      landmarkId: params.landmarkId?.split(',')[0], // First landmark ID (single)
      landmarkIds: params.landmarkId ? params.landmarkId.split(',').filter(Boolean) : undefined, // All landmark IDs (array)
      propertyId: params.propertyId,
      bounds: params.bounds,
      checkIn: searchParams.dates.checkIn,
      checkOut: searchParams.dates.checkOut,
      roomRateId: searchParams.roomRateId,
      roomTypeId: searchParams.roomTypeId,
      flexibleMonths: searchParams.flexibleSearch.flexibleMonths,
      flexibleNights: searchParams.flexibleSearch.flexibleNights,
      userSelectedFlexibleMonths: searchParams.flexibleSearch.userSelectedFlexibleMonths,
      rooms: searchParams.rooms,
      amenities: params.amenities ? params.amenities.split(',') : [],
      inclusions: params.inclusions ? params.inclusions.split(',') : [],
      tags: params.tags ? params.tags.split(',') : [],
      offerTags: params.offerTags ? params.offerTags.split(',') : [],
      limit: params.limit ? parseInt(params.limit, 10) : undefined,
      customerRatingGte: searchParams.customerRatingGte ? Number(searchParams.customerRatingGte) : undefined,
      priceGte: params.priceGte,
      priceLte: params.priceLte,
      operators: params.operators ? params.operators.split(',') : [],
      tourLengthGte: params.tourLengthGte,
      tourLengthLte: params.tourLengthLte,
      destinations: params.destinations && isString(params.destinations) ?
          params.destinations.split(',') :
        params.destinations && Array.isArray(params.destinations) ?
          params.destinations : [],
      // 'categories' is also used for experience lists and they are an -array- of string instead
      // this one is used for tour categories
      categories: typeof params.categories === 'string' ? params.categories.split(',') : [],
      offerTypes: searchParams.offerType ? searchParams.offerType.split(',') as Array<App.OfferListOfferType> : undefined,
      // @ts-expect-error
      productTypes: params.productTypes,
      bedroomsGte: params.bedroomsGte ? parseInt(params.bedroomsGte) : undefined,
      bedroomsEq: params.bedroomsEq ? parseInt(params.bedroomsEq) : undefined,
      bedsGte: params.bedsGte ? parseInt(params.bedsGte) : undefined,
      bedsEq: params.bedsEq ? parseInt(params.bedsEq) : undefined,
      bathroomsGte: params.bathroomsGte ? parseInt(params.bathroomsGte) : undefined,
      bathroomsEq: params.bathroomsEq ? parseInt(params.bathroomsEq) : undefined,
      onSale: params.onSale === 'true' ? true : undefined,
      flashOffer: params.flashOffer === 'true' ? true : undefined,
      luxPlusFeatures: params.luxPlusFeatures?.split(',') as Array<App.LuxPlusFeatureFilter> ?? [],
      agentHubFeatures: params.agentHubFeatures?.split(',') as Array<App.AgentHubFeatureFilter> ?? [],
      tourStyles: params.tourStyles?.split(',') ?? EmptyArray,
      source: params.source,
      loyalty: params[LOYALTY_SEARCH_FILTER_PARAM]?.split(','),
      paymentOptions: params.paymentOptions?.split(','),
      lat: params.lat ? parseFloat(params.lat) : undefined,
      lng: params.lng ? parseFloat(params.lng) : undefined,
      query: params.query,
    }
  },
)

/**
 * Check if current route is a specific campaign page
 * Memoized selector that takes specificCampaign as a parameter
 *
 * @param state App.State
 * @param specificCampaign string - the campaign name to check
 * @returns boolean
 */
export const getIsSpecificCampaignPage = createSelector(
  (state: App.State) => state.router.location.pathname,
  (_state: App.State, specificCampaign: string) => specificCampaign,
  (pathname, specificCampaign) => {
    const isCampaignPage = matchPath(pathname, { path: '/:regionCode/campaign', exact: false })
    return isCampaignPage ? last(pathname.split('/')) === specificCampaign : false
  }
)
